import { useReducer, useCallback, useEffect, useRef } from "react"
import dayjs from "dayjs"
import { calculateMinWidth } from "src/utils/table-render-functions"
import { DATE_FORMAT } from "src/utils/constants"
import { formatCpfCnpj } from "src/utils/format-cpf-cnpj"

const initialState = {
    loading: false,
    rows: [],
    table: [],
    alert: { open: false, message: "", type: "" },
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_TABLE":
            return { ...state, table: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") => {
    return [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)
        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)
        return order === "desc" ? dateB - dateA : dateA - dateB
    })
}

export const useRatingEmissaoTable = (
    emissaoService,
    cetipIsin,
    dadosEmpresa
) => {
    const [state, dispatch] = useReducer(reducer, initialState)
     // garante que os dados do cetip anterior não preencham a tabela ao mudar o cetip
    const loadedRef = useRef(cetipIsin)
    
    useEffect(() => {
        if (loadedRef.current === cetipIsin) return

        loadedRef.current = cetipIsin
    
        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "SET_TABLE", payload: []})
        dispatch({ type: "CLOSE_ALERT" })
            
    }, [cetipIsin])

    const buildTable = useCallback(
        (setor, subsetor) => {
            dispatch({
                type: "SET_TABLE",
                payload: [
                    {
                        cells: [
                            `CETIP: ${dadosEmpresa?.CETIP}`,
                            `ISIN: ${dadosEmpresa?.ISIN}`,
                            `Vencimento: ${
                                dadosEmpresa?.DataVencimento
                                    ? dayjs(dadosEmpresa.DataVencimento).format(
                                          "DD/MM/YYYY"
                                      )
                                    : ""
                            }`,
                            `Data Resgate Antecipado: ${dadosEmpresa?.DataResgateAntecipado ? dayjs(dadosEmpresa.DataResgateAntecipado).format("DD/MM/YYYY") : ""}`,
                        ],
                    },
                    {
                        cells: [
                            `CPF/CNPJ: ${formatCpfCnpj(dadosEmpresa?.CPF_CNPJ_Devedor)}`,
                            `Devedor: ${dadosEmpresa?.Devedor || ""}`,
                            `Setor: ${setor || ""}`,
                            `Subsetor: ${subsetor || ""}`,
                        ],
                    },
                ],
            })
        },
        [cetipIsin, dadosEmpresa]
    )

    const loadTableRows = useCallback(() => {
        dispatch({ type: "SET_LOADING", payload: true })
        emissaoService
            .ratingEmissao("get", `?CETIP=${cetipIsin?.toUpperCase()}`)
            .then(({ body }) => {
                const rows = ordenaPorDataReferencia(body.results, "desc").map(
                    (obj, i) => ({ ...obj, id: i })
                )

                const { Setor, SubSetor } = rows[0] || {}
                buildTable(Setor, SubSetor)
                dispatch({ type: "SET_ROWS", payload: rows })
            })
            .catch(() => {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CETIP consultado.",
                        type: "warning",
                    },
                })
            })
            .finally(() => dispatch({ type: "SET_LOADING", payload: false }))
    }, [cetipIsin, emissaoService, buildTable])

    const addRow = useCallback(async () => {
        let lastItem = state.rows[0]

        if (state.rows.length === 0) {
            lastItem = {
                CETIP: cetipIsin,
                CPFCNPJ:
                    dadosEmpresa?.CPF_CNPJ_Devedor || dadosEmpresa?.CPFCNPJ,
                DataReferencia: dayjs().format(DATE_FORMAT),
                Devedor: dadosEmpresa?.Devedor || "N/A",
                Setor: "N/A",
                SubSetor: "N/A",
                Fonte: "N/A",
                Rating: "N/A",
            }
        }

        dispatch({ type: "SET_LOADING", payload: true })

        try {
            await emissaoService.ratingEmissao("post", lastItem)
            loadTableRows()
        } catch (error) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao adicionar novo registro.",
                    type: "error",
                },
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [state.rows, cetipIsin, dadosEmpresa, emissaoService, loadTableRows])

    const deleteRow = useCallback(
        async (row) => {
            const { ID } = row

            dispatch({ type: "SET_LOADING", payload: true })

            try {
                await emissaoService.ratingEmissao("delete", ID)
                loadTableRows()
            } catch (error) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao deletar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const updateRow = useCallback(
        async (row) => {
            const { ID } = row

            dispatch({ type: "SET_LOADING", payload: true })

            try {
                await emissaoService.ratingEmissao("patch", ID, row)

                const updatedRows = state.rows.map((existingRow) =>
                    existingRow.ID === ID
                        ? { ...existingRow, ...row }
                        : existingRow
                )
                dispatch({ type: "SET_ROWS", payload: updatedRows })

                return row
            } catch (error) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao atualizar registro.",
                        type: "error",
                    },
                })
                throw error
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, state.rows]
    )

    return {
        state: {
            ...state,
            minWidthAnaliseCredito: calculateMinWidth(
                state.rows,
                "AnaliseCreditoEmissao"
            ),
            minWidthObservacao: calculateMinWidth(state.rows, "Observacao"),
        },
        actions: {
            loadTableRows,
            loadTableRowsDevedor: loadTableRows,
            setAlert: (payload) => dispatch({ type: "SET_ALERT", payload }),
            closeAlert: () => dispatch({ type: "CLOSE_ALERT" }),
            addRow,
            deleteRow,
            updateRow,
            setRows: (rows) => dispatch({ type: "SET_ROWS", payload: rows }),
        },
    }
}
